import { useMemo, useState } from 'react';
import { usePredictionMarketEventsFindLobby } from '@/lib/endpoints';
import type { OrderSide } from '@/analytics';
import type { EventCardDto, MarketOptionDto } from '@/lib/models';
import { SectionHeader } from '@/components/ui';
import { BinaryQuestionCard } from '../BinaryMarkets/BinaryQuestionCard';
import { BuyInPopup, type BuyMarketContext } from '../BinaryMarkets/BuyInPopup';
import { SportsMoneylineCard } from '../SportsMarkets/SportsMoneylineCard';
import { SnakeLobbyFeed } from '../SnakeMarkets';
import { FtueLobbySkeleton } from './FtueLobbySkeleton';

/** Popup target carrying the resolved option context + opening side. */
interface BuyTarget {
  context: BuyMarketContext;
  side: OrderSide;
}

/** How many cards the curated shelf shows below the hero. */
const SHELF_SIZE = 3;

/**
 * The `make-a-call` arm of `exp.ftue.experience` — the newcomer lobby.
 *
 * Thesis under test: a first-visit user converts better when the lobby
 * produces ONE obvious action instead of a catalog. So, versus the control
 * (`Trending`): one hero question instead of an auto-sliding carousel, the
 * Snake widget as the single live element (the 5-minute feedback loop), one
 * small curated shelf, and nothing else — no banner, no per-category
 * sections, no explore block.
 *
 * Deliberately reuses the production card mechanics (`BinaryQuestionCard`,
 * `BuyInPopup`, same `usePredictionMarketEventsFindLobby` fetch — React Query
 * shares the cache with the control arm), so the experiment measures the
 * *experience*, not a divergent implementation.
 */
export function MakeACallLobby() {
  const { data, isLoading, isError } = usePredictionMarketEventsFindLobby({ limit: 50 });
  const [buyTarget, setBuyTarget] = useState<BuyTarget | null>(null);

  const { hero, shelf } = useMemo(() => {
    const events = data?.events ?? [];
    // The hero should be a clean one-tap yes/no question: prefer the first
    // single-BINARY-market event (inline buy), fall back to the first event.
    const heroEvent = events.find((e) => e.isSingleMarket && e.markets[0]?.renderKind === 'BINARY') ?? events[0];
    const rest = events.filter((e) => e !== heroEvent).slice(0, SHELF_SIZE);
    return { hero: heroEvent, shelf: rest };
  }, [data]);

  function handleOpenBuy(event: EventCardDto, option: MarketOptionDto, side: OrderSide) {
    setBuyTarget({ context: { ...option, eventName: event.name }, side });
  }

  if (isLoading) return <FtueLobbySkeleton />;

  if (isError || !hero) {
    return <div className="w-full py-6 text-center text-sm text-muted">Failed to load markets.</div>;
  }

  const renderCard = (event: EventCardDto) =>
    event.sportsMoneyline ? (
      <SportsMoneylineCard
        event={event}
        moneyline={event.sportsMoneyline}
        onOpenBuy={(option, side) => handleOpenBuy(event, option, side)}
      />
    ) : (
      <BinaryQuestionCard event={event} onOpenBuy={(option, side) => handleOpenBuy(event, option, side)} />
    );

  return (
    <div
      data-testid="ftue-make-a-call"
      className="w-full px-3.5 md:px-0 md:max-w-[760px] xl:max-w-[1300px] mx-auto flex flex-col gap-4 md:gap-5 pb-6"
    >
      {/* Hero — one question, one obvious action. */}
      <section data-testid="ftue-hero" className="flex w-full flex-col gap-2.5 md:max-w-[760px] md:mx-auto">
        <div className="flex flex-col gap-0.5">
          <h1 className="m-0 text-[22px] md:text-[26px] font-semibold tracking-[-0.44px] text-ink">Make your first call</h1>
          <p className="m-0 text-sm text-ink-muted">Pick a side — if you're right, you get paid. That's the whole game.</p>
        </div>
        {renderCard(hero)}
      </section>

      {/* The single live element: 5-minute rounds, the fast feedback loop. */}
      <SnakeLobbyFeed />

      {/* One curated shelf — not a catalog. */}
      {shelf.length > 0 && (
        <section className="flex w-full flex-col gap-2.5">
          <SectionHeader title="Start simple" />
          <div className="flex flex-col gap-2.5 md:grid md:grid-cols-2 md:items-stretch md:gap-4 xl:grid-cols-3">
            {shelf.map((event) => (
              <div key={event.id} className="h-full">
                {renderCard(event)}
              </div>
            ))}
          </div>
        </section>
      )}

      <BuyInPopup
        market={buyTarget?.context ?? null}
        initialSide={buyTarget?.side ?? 'yes'}
        surface="home.card"
        onClose={() => setBuyTarget(null)}
      />
    </div>
  );
}
